/** @fileoverview Tracks the files attached to the composer: upload, process, then index in the background. */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  attachmentErrorMessage,
  attachmentKind,
  documentMediaType,
  documentProblem,
  isFinalFailure,
  processFailureMessage,
  MAX_ATTACHMENTS_PER_MESSAGE,
  WHOLE_FILE_TOKEN_LIMIT,
} from '../lib/attachments';
import { imageErrorMessage, prepareImage } from '../lib/imageAttachments';
import { ApiError } from '../services/platformAuth';
import {
  createAttachment,
  deleteAttachment,
  getAttachmentStatus,
  processAttachment,
  putToStorage,
} from '../services/attachmentsApi';
import type { AttachmentKind, IndexStatus } from '../types/attachments';
import type { ChatAttachment } from '../types/chat';

export type ComposerAttachment = {
  key: string;
  kind: AttachmentKind;
  name: string;
  mediaType: string;
  previewUrl?: string;
  status: 'preparing' | 'uploading' | 'processing' | 'ready' | 'failed';
  /** Set once stored; this is what the message carries. */
  id?: string;
  error?: string;
  /** The failure cannot be fixed by retrying; the file has to be removed. */
  final?: boolean;
  pages?: number;
  tokens?: number;
  /** Extraction notes for the user, such as OCR pages that may contain errors. */
  notes?: string[];
  /** Documents only: background embedding after Ready. */
  indexStatus?: IndexStatus;
};

const INDEX_POLL_MS = 2000;
const INDEX_POLL_LIMIT = 150;
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const INDEX_FAILED = 'Indexing failed. Retry to try again.';

const isLarge = ({ kind, tokens }: ComposerAttachment) =>
  kind === 'document' && (tokens ?? 0) > WHOLE_FILE_TOKEN_LIMIT;

/** A file over the whole-file limit is only reachable through search, so Send waits for its index. */
const needsIndex = (item: ComposerAttachment) => isLarge(item) && item.indexStatus !== 'indexed';

const isIndexing = (item: ComposerAttachment) =>
  item.status === 'ready' && item.indexStatus === 'indexing';

/** Uploads attached files to `conversationId` right away; Send waits until each one is Ready. */
export function useAttachments(
  tenantId: string,
  conversationId: string,
  onError: (message: string) => void
) {
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const conversationRef = useRef(conversationId);
  conversationRef.current = conversationId;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  // What was picked, kept so Retry can upload again if the first call never finished.
  const sources = useRef(new Map<string, File>());
  // The running upload per chip, so removing the chip cancels it.
  const uploads = useRef(new Map<string, AbortController>());

  const update = useCallback((key: string, patch: Partial<ComposerAttachment>) => {
    setAttachments((current) =>
      current.map((item) => (item.key === key ? { ...item, ...patch } : item))
    );
  }, []);

  // Files belong to the conversation they were uploaded to, so switching drops them.
  useEffect(
    () => () => {
      for (const item of attachmentsRef.current) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }
      sources.current.clear();
      uploads.current.forEach((controller) => controller.abort());
      uploads.current.clear();
      // Reset now: after an unmount no render refreshes the ref, so a running poll would go on.
      attachmentsRef.current = [];
      setAttachments([]);
    },
    [conversationId, tenantId]
  );

  const remove = useCallback(
    (key: string) => {
      const item = attachmentsRef.current.find((entry) => entry.key === key);
      if (!item) return;
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      sources.current.delete(key);
      uploads.current.get(key)?.abort();
      uploads.current.delete(key);
      attachmentsRef.current = attachmentsRef.current.filter((entry) => entry.key !== key);
      setAttachments((current) => current.filter((entry) => entry.key !== key));
      if (!item.id) return;
      void deleteAttachment(tenantId, conversationRef.current, item.id).catch(() => {});
    },
    [tenantId]
  );

  /** Follows a document's background indexing; only a file over the whole-file limit fails on it. */
  const pollIndex = useCallback(
    async (key: string, id: string, target: string) => {
      const stillHere = () =>
        conversationRef.current === target && attachmentsRef.current.some((i) => i.key === key);
      for (let attempt = 0; attempt < INDEX_POLL_LIMIT; attempt += 1) {
        await wait(INDEX_POLL_MS);
        if (!stillHere()) return;
        try {
          const { indexStatus } = await getAttachmentStatus(tenantId, target, id);
          if (!stillHere()) return;
          // A small file can still be read whole, so a failed or missing index only unblocks Send.
          update(key, { indexStatus: indexStatus === 'none' ? 'failed' : indexStatus });
          if (indexStatus === 'indexing') continue;
          const item = attachmentsRef.current.find((i) => i.key === key);
          if (item && isLarge(item) && indexStatus !== 'indexed') {
            update(key, { status: 'failed', error: INDEX_FAILED });
          }
          return;
        } catch {
          // A missed poll is not fatal; try again until the limit.
        }
      }
      // Out of attempts: a large file still not indexed would block Send forever, so surface Retry.
      const item = attachmentsRef.current.find((i) => i.key === key);
      if (stillHere() && item && needsIndex(item)) {
        update(key, { status: 'failed', error: INDEX_FAILED });
      } else if (stillHere() && item?.indexStatus === 'indexing') {
        update(key, { indexStatus: 'failed' });
      }
    },
    [tenantId, update]
  );

  /** Runs the steps still missing for `key`: create and upload (if not stored yet), then process. */
  const run = useCallback(
    async (key: string) => {
      const file = sources.current.get(key);
      const target = conversationRef.current;
      const existing = attachmentsRef.current.find((item) => item.key === key);
      if (!file || !existing) return;
      const gone = () =>
        conversationRef.current !== target || !attachmentsRef.current.some((i) => i.key === key);
      let id = existing.id;
      const fail = (message: string, final = false) => {
        update(key, { status: 'failed', error: message, final });
        onErrorRef.current(message);
      };
      try {
        if (!id) {
          let body: Blob = file;
          if (existing.kind === 'image') {
            update(key, { status: 'preparing', error: undefined });
            body = await prepareImage(file);
            if (gone()) return;
          }
          update(key, { status: 'uploading', error: undefined, final: false });
          const created = await createAttachment(tenantId, target, {
            name: file.name,
            size: body.size,
            mediaType: existing.kind === 'image' ? body.type : documentMediaType(file),
          });
          if (gone()) {
            void deleteAttachment(tenantId, target, created.id).catch(() => {});
            return;
          }
          const controller = new AbortController();
          uploads.current.set(key, controller);
          try {
            await putToStorage(created.upload, body, controller.signal);
          } catch (error) {
            // Not stored, so the created record is useless; the next try asks for a fresh URL.
            void deleteAttachment(tenantId, target, created.id).catch(() => {});
            throw error;
          } finally {
            uploads.current.delete(key);
          }
          id = created.id;
          update(key, { id });
          if (gone()) {
            void deleteAttachment(tenantId, target, id).catch(() => {});
            return;
          }
        }
        update(key, { status: 'processing', error: undefined, final: false });
        const result = await processAttachment(tenantId, target, id);
        if (gone()) return;
        if (result.status === 'failed') {
          if (result.reason === 'NOT_UPLOADED') {
            // The bytes never arrived: forget this record so Retry uploads again.
            update(key, { id: undefined });
            void deleteAttachment(tenantId, target, id).catch(() => {});
          }
          fail(processFailureMessage(result.reason), isFinalFailure(result.reason));
          return;
        }
        update(key, {
          status: 'ready',
          pages: result.pages,
          tokens: result.tokens,
          notes: result.notes,
          indexStatus: result.indexStatus,
        });
        if (result.indexStatus === 'indexing') {
          void pollIndex(key, id, target);
        } else if ((result.tokens ?? 0) > WHOLE_FILE_TOKEN_LIMIT) {
          // Search is the only way to read this file, and indexing could not be queued.
          update(key, { status: 'failed', error: INDEX_FAILED });
        }
      } catch (error) {
        if (gone()) return;
        fail(
          existing.kind === 'image' && !(error instanceof ApiError)
            ? imageErrorMessage(error)
            : attachmentErrorMessage(error)
        );
      }
    },
    [pollIndex, tenantId, update]
  );

  const add = useCallback(
    (files: File[]) => {
      const supported = files.filter((file) => attachmentKind(file));
      if (supported.length < files.length) {
        onErrorRef.current('Use a PDF, CSV, text, PNG, JPEG or WebP file.');
      }
      const sized = supported.filter((file) => {
        const problem = attachmentKind(file) === 'document' ? documentProblem(file) : undefined;
        if (problem) onErrorRef.current(problem);
        return !problem;
      });
      const room = Math.max(0, MAX_ATTACHMENTS_PER_MESSAGE - attachmentsRef.current.length);
      if (sized.length > room) {
        onErrorRef.current(
          `You can attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`
        );
      }
      const added = sized.slice(0, room).map((file): ComposerAttachment => {
        const kind = attachmentKind(file) as AttachmentKind;
        return {
          key: crypto.randomUUID(),
          kind,
          name: file.name,
          mediaType: file.type,
          previewUrl: kind === 'image' ? URL.createObjectURL(file) : undefined,
          status: kind === 'image' ? 'preparing' : 'uploading',
        };
      });
      // Updated now, not on the next render, so a second add in the same tick sees the new count.
      attachmentsRef.current = [...attachmentsRef.current, ...added];
      setAttachments((current) => [...current, ...added]);
      added.forEach((item, index) => {
        sources.current.set(item.key, sized[index]);
        void run(item.key);
      });
    },
    [run]
  );

  /** Re-runs only what failed: process if the file is stored, otherwise a fresh create and upload. */
  const retry = useCallback((key: string) => void run(key), [run]);

  /** Forgets the chips once a message carries them; their local previews stay alive to be shown. */
  const clear = useCallback(() => {
    attachmentsRef.current = [];
    sources.current.clear();
    setAttachments([]);
  }, []);

  return {
    attachments,
    add,
    remove,
    retry,
    clear,
    /** The stored files, in the form a message carries. */
    ready: attachments.flatMap(
      ({ id, previewUrl, kind, name, mediaType, pages, notes }): ChatAttachment[] =>
        id ? [{ id, previewUrl, type: kind, name, mediaType, pages, notes }] : []
    ),
    /** True while any file is not Ready, or a document is still indexing. */
    blocking: attachments.some(
      (item) => item.status !== 'ready' || needsIndex(item) || isIndexing(item)
    ),
  };
}
